import React, { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import Button from "@/components/buttons/Button";
import Checkbox from "@/components/inputs/Checkbox";
import { RightsShare, RightsSummary } from "@/pages/Tracks/Contracts/types";
import FormActions from "@/components/buttons/FormActions";

interface LockAndIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  issueUrl: string;
  representativeId: string;
  representativeName: string;
  contractTitles: string[];
  rightsSummary: RightsSummary;
}

const RightsSection: React.FC<{ heading: string; shares: RightsShare[] }> = ({
  heading,
  shares,
}) => {
  if (shares.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <p className="text-cream-600 dark:text-cream-400 text-sm font-medium">
        {heading}
      </p>
      {shares.map((share) => (
        <div
          key={share.name}
          className="text-cream-900 dark:text-cream-100 flex items-baseline justify-between gap-4"
        >
          <span>{share.name}</span>
          <span className="font-medium">{share.split_percentage}%</span>
        </div>
      ))}
    </div>
  );
};

const LockAndIssueModal: React.FC<LockAndIssueModalProps> = ({
  isOpen,
  onClose,
  issueUrl,
  representativeId,
  representativeName,
  contractTitles,
  rightsSummary,
}) => {
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) setConfirmed(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleIssue = () => {
    router.post(
      issueUrl,
      { representative_id: representativeId },
      {
        onStart: () => setProcessing(true),
        onSuccess: () => onClose(),
        onFinish: () => setProcessing(false),
      },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleOverlayClick}
    >
      <div className="dark:bg-cream-900 mx-4 flex max-h-[85vh] w-full max-w-md flex-col rounded-2xl bg-white shadow-xl">
        <div className="shrink-0 p-6 pb-0">
          <h2 className="font-sans text-xl font-bold">Lock and issue</h2>
        </div>
        <div className="flex min-h-0 flex-1 flex-col px-6 py-4">
          <p className="text-cream-700 dark:text-cream-400 shrink-0">
            Locking freezes the contract content and prepares it for signing.
            This cannot be undone, even if the track splits change later.
          </p>
          <p className="text-cream-800 dark:text-cream-200 mt-4 shrink-0 font-medium">
            The following contracts will be locked:
          </p>
          <ul className="text-cream-700 dark:text-cream-400 mt-2 shrink-0 list-disc pl-5">
            {contractTitles.map((title) => (
              <li key={title}>{title}</li>
            ))}
          </ul>
          <div className="border-cream-300 dark:border-cream-800 dark:bg-cream-950 bg-cream-100 mt-4 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto rounded-2xl border p-6">
            {representativeName && (
              <div className="border-cream-300 dark:border-cream-800 flex items-baseline justify-between gap-4 border-b pb-4">
                <span className="text-cream-800 dark:text-cream-200">
                  Altar representative
                </span>
                <span className="text-cream-900 dark:text-cream-100 font-medium">
                  {representativeName}
                </span>
              </div>
            )}
            <RightsSection
              heading="Master rights"
              shares={rightsSummary.master_owners}
            />
            {rightsSummary.master_owners.length > 0 &&
              rightsSummary.writers.length > 0 && (
                <div className="border-cream-300 dark:border-cream-800 border-t" />
              )}
            <RightsSection
              heading="Publishing rights"
              shares={rightsSummary.writers}
            />
          </div>
        </div>
        <div className="border-cream-200 dark:border-cream-800 shrink-0 border-t p-6">
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            checkboxLabel="I've reviewed all contracts in this bundle."
          />
          <FormActions className="mt-6">
            <Button
              text="Cancel"
              type="secondary"
              htmlButtonType="button"
              onClick={onClose}
              disabled={processing}
            />
            <Button
              text={processing ? "Locking..." : "Lock and issue"}
              type="primary"
              htmlButtonType="button"
              onClick={handleIssue}
              disabled={!confirmed || processing}
            />
          </FormActions>
        </div>
      </div>
    </div>
  );
};

export default LockAndIssueModal;
